'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHasWebGameConsent } from '@/features/consent/shared/use-has-web-game-consent';
import { setContinueToPlayGameCard } from '@/features/games/shared/state/game-actions';
import { GameData } from '@/features/games/shared/types/game-data';
import { gameDataToGameCard } from '@/features/games/internal/helpers/game-data-to-game-card-data';

type WebGameFrameProps = {
  src: string;
  game: GameData;
};

export const WebGameFrame = ({ src, game }: WebGameFrameProps) => {
  const scrollLockRef = useRef<string | null>(null);
  const ensureHttps = (url: string) => {
    if (typeof url !== 'string') return url; // guard clause

    // Trim leading/trailing spaces
    const trimmedUrl = url.trim();

    // If it starts with "http://" but not "https://"
    if (trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
      return 'https://' + trimmedUrl.slice(7); // replace "http://" with "https://"
    }

    return trimmedUrl;
  };

  const hasConsent = useHasWebGameConsent();
  const gameCard = useMemo(() => gameDataToGameCard(game), [game]);
  const lockScroll = useCallback(() => {
    if (typeof document === 'undefined') return;
    const { body } = document;
    if (body.dataset.webGameScrollLock === 'true') return;
    scrollLockRef.current = body.style.overflow;
    body.dataset.webGameScrollLock = 'true';
    body.style.overflow = 'hidden';
  }, []);

  const unlockScroll = useCallback(() => {
    if (typeof document === 'undefined') return;
    const { body } = document;
    if (body.dataset.webGameScrollLock !== 'true') return;
    body.style.overflow = scrollLockRef.current ?? '';
    delete body.dataset.webGameScrollLock;
    scrollLockRef.current = null;
  }, []);

  useEffect(() => {
    if (!hasConsent) return;
    setContinueToPlayGameCard(gameCard);
  }, [gameCard, hasConsent]);

  useEffect(() => {
    return () => unlockScroll();
  }, [unlockScroll]);

  if (!hasConsent) {
    return null;
  }

  return (
    <iframe
      src={ensureHttps(src)}
      title={`Play ${game.name}`}
      className='h-full w-full border-0'
      loading='eager'
      allow='autoplay; gamepad; geolocation; clipboard-read; clipboard-write'
      referrerPolicy='no-referrer-when-downgrade'
      onFocus={lockScroll}
      onBlur={unlockScroll}
      onMouseEnter={lockScroll}
      onMouseLeave={unlockScroll}
      onTouchStart={lockScroll}
      onTouchEnd={unlockScroll}
      onTouchCancel={unlockScroll}
    />
  );
};
